
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { FileText, MessageSquare, Calendar, TrendingUp } from "lucide-react";
import { format, subDays } from "date-fns";

interface UsageStats {
  totalDocuments: number;
  totalChats: number;
  documentsThisWeek: number;
  chatsThisWeek: number;
  lastLoginDate: string;
  mostActiveDay: string;
}

export const UsageAnalytics = () => {
  const [stats, setStats] = useState<UsageStats>({
    totalDocuments: 0,
    totalChats: 0,
    documentsThisWeek: 0,
    chatsThisWeek: 0,
    lastLoginDate: "Today",
    mostActiveDay: "Monday"
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const weekAgo = subDays(new Date(), 7);

      // Fetch total documents
      const { count: totalDocs } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id);

      // Fetch documents this week
      const { count: docsThisWeek } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .gte('created_at', weekAgo.toISOString());

      // Fetch total chat sessions
      const { count: totalChats } = await supabase
        .from('chat_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id);

      // Fetch chat sessions this week
      const { count: chatsThisWeek } = await supabase
        .from('chat_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .gte('created_at', weekAgo.toISOString());

      // Get most recent document to determine activity
      const { data: recentDoc } = await supabase
        .from('documents')
        .select('created_at')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const lastActivity = recentDoc?.created_at 
        ? format(new Date(recentDoc.created_at), 'MMM d')
        : "No activity yet";

      setStats({
        totalDocuments: totalDocs || 0,
        totalChats: totalChats || 0,
        documentsThisWeek: docsThisWeek || 0,
        chatsThisWeek: chatsThisWeek || 0,
        lastLoginDate: lastActivity,
        mostActiveDay: "This week"
      });
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4 md:space-y-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Your Progress</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse border-0 shadow-md">
              <CardHeader className="pb-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4 md:space-y-6">
      <h2 className="text-xl md:text-2xl font-bold text-gray-900">Your Progress</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 bg-gradient-to-br from-blue-50 to-white min-h-[120px] md:min-h-[140px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 md:p-6">
            <CardTitle className="text-xs md:text-sm font-semibold text-gray-700 leading-tight">Total Documents</CardTitle>
            <div className="p-1.5 md:p-2 bg-blue-100 rounded-full">
              <FileText className="h-3 w-3 md:h-4 md:w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 p-4 md:p-6 md:pt-0">
            <div className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{stats.totalDocuments}</div>
            <p className="text-xs text-gray-600 flex items-center">
              <span className="text-green-600 font-medium">+{stats.documentsThisWeek}</span>
              <span className="ml-1">this week</span>
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 bg-gradient-to-br from-purple-50 to-white min-h-[120px] md:min-h-[140px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 md:p-6">
            <CardTitle className="text-xs md:text-sm font-semibold text-gray-700 leading-tight">AI Conversations</CardTitle>
            <div className="p-1.5 md:p-2 bg-purple-100 rounded-full">
              <MessageSquare className="h-3 w-3 md:h-4 md:w-4 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 p-4 md:p-6 md:pt-0">
            <div className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{stats.totalChats}</div>
            <p className="text-xs text-gray-600 flex items-center">
              <span className="text-green-600 font-medium">+{stats.chatsThisWeek}</span>
              <span className="ml-1">this week</span>
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 bg-gradient-to-br from-green-50 to-white min-h-[120px] md:min-h-[140px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 md:p-6">
            <CardTitle className="text-xs md:text-sm font-semibold text-gray-700 leading-tight">Last Activity</CardTitle>
            <div className="p-1.5 md:p-2 bg-green-100 rounded-full">
              <Calendar className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 p-4 md:p-6 md:pt-0">
            <div className="text-xl md:text-2xl font-bold text-green-600 mb-1">Active</div>
            <p className="text-xs text-gray-600">
              {stats.lastLoginDate}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 bg-gradient-to-br from-orange-50 to-white min-h-[120px] md:min-h-[140px]">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 md:p-6">
            <CardTitle className="text-xs md:text-sm font-semibold text-gray-700 leading-tight">Weekly Growth</CardTitle>
            <div className="p-1.5 md:p-2 bg-orange-100 rounded-full">
              <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 p-4 md:p-6 md:pt-0">
            <div className="text-xl md:text-2xl font-bold text-orange-600 mb-1">
              {stats.documentsThisWeek + stats.chatsThisWeek}
            </div>
            <p className="text-xs text-gray-600">
              Actions this week
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
